import React from 'react';
import { useTranslation } from '@lib/useTypedTranslation';
import styled from 'styled-components';

import { ChipButton } from '../controls/chipButton';
import { OptionPicker } from '../controls/optionPicker';
import { IHomeLocation } from '../../services/config/config';
import {
  Group,
  Option,
  getHomeLocationSuggestionClasses,
  getGroupSuggestionClasses,
  Zone
} from '../controls/optionPickerLib';
import { ButtonColours, Theme } from '../../containers/app/themes';
import { Filter } from './useFilterPicker';
import { ITimePeriodPickerProps, TimePeriod } from './useTimePeriodPicker';
import { Button, Variants } from '@components/controls/button';

import "./timePeriodAndFilterPicker.css";

const Title = styled.div`
    font-weight: ${({ theme }: { theme: Theme}) => theme.font.weights.semiBold};
`;

const ParentContainer = styled.div`
  display: flex;
  padding: 0 2.5rem 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const ChildrenContainer = styled.div`
  display: flex;
  align-items: center;

  &:not(:last-child) {
    margin-right: 2rem;
  }

  & > *:not(:last-child) {
    margin-right: 0.75rem;
  }
`;

export const TimePeriodPicker: React.FC<ITimePeriodPickerProps> = props => {
  const { t } = useTranslation('filters');
  const {
    timePeriod,
    timePeriodOptions,
    handleTimePeriodSelect
  } = props;

  return <>
    <Title>{t('TIME_PERIOD')}</Title>
    <OptionPicker<TimePeriod>
      id="timePeriodPicker"
      options={timePeriodOptions}
      current={timePeriod}
      onSelect={handleTimePeriodSelect}
      selectText={t('SELECT_TIME_PERIOD')}
      inactive={!timePeriod}
    />
  </>;
};

export interface TimePeriodAndFilterPickerProps<AdditionalFilterTypes = any> {
  filter: Filter,
  homeLocationOptions: Option<IHomeLocation>[],
  groupOptions: Option<Group>[],
  zoneOptions: Option<Zone>[],
  allFilterActive: boolean,
  groupActive: boolean,
  homeLocationActive: boolean,
  zoneActive: boolean,
  homeLocationPickerVisible: boolean,
  zonePickerVisible: boolean,
  modelOptions?: Option<string>[],
  modelActive?: boolean,
  modelPickerVisible?: boolean,
  handleFilterReset: () => void,
  handleHomeLocationSelect: (selected: Option<IHomeLocation>) => void,
  handleGroupSelect: (selected: Option<Group>) => void,
  handleZoneSelect: (selected: Option<Zone>) => void,
  handleModelSelect?: (selected: Option<string>) => void,
  timePeriod: Option<TimePeriod>,
  timePeriodOptions: Option<TimePeriod>[],
  handleTimePeriodSelect: (selected: Option<TimePeriod>) => void,
  className?: string,
  additionalFilters?: {
    id: string,
    options: Option<AdditionalFilterTypes>[],
    handleFilterSelect: (selected: Option<AdditionalFilterTypes>) => void,
    current: Option<AdditionalFilterTypes>,
    active: boolean,
    selectText: string,
    title?: string
  }[]
}

export function TimePeriodAndFilterPicker<AdditionalFilterTypes>(props: TimePeriodAndFilterPickerProps<AdditionalFilterTypes>) {
  const { t } = useTranslation('filters');

  const {
    filter,
    homeLocationOptions,
    groupOptions,
    zoneOptions,
    allFilterActive,
    groupActive,
    homeLocationActive,
    zoneActive,
    homeLocationPickerVisible,
    zonePickerVisible,
    modelOptions = [],
    modelActive = false,
    modelPickerVisible,
    handleFilterReset,
    handleHomeLocationSelect,
    handleGroupSelect,
    handleZoneSelect,
    handleModelSelect,
    timePeriod,
    timePeriodOptions,
    handleTimePeriodSelect,
    className
  } = props;

  return (
    <ParentContainer className={className}>
      <ChildrenContainer>
        <TimePeriodPicker {...{
          timePeriod,
          timePeriodOptions,
          handleTimePeriodSelect
        }} />
      </ChildrenContainer>
      <ChildrenContainer>
        <Title>{t('FILTER_BY')}</Title>
        <ChipButton
          onClick={handleFilterReset}
          text={t('ALL_DEVICES')}
          active={allFilterActive}
        />
        {modelPickerVisible && handleModelSelect && <OptionPicker<string>
          id="modelPicker"
          options={modelOptions}
          current={filter.model}
          onSelect={handleModelSelect}
          disabled={!modelOptions.length}
          selectText={t('SELECT_MODEL')}
          isSearchable
          inactive={!modelActive}
        />}
        {homeLocationPickerVisible && <OptionPicker<IHomeLocation>
          id="homeLocationPicker"
          options={homeLocationOptions}
          current={filter.homeLocation}
          onSelect={handleHomeLocationSelect}
          disabled={!homeLocationOptions.length}
          getSuggestionClasses={getHomeLocationSuggestionClasses}
          selectText={t('SELECT_HOME_LOCATION')}
          isSearchable
          inactive={!homeLocationActive}
        />}
        <OptionPicker<Group>
          id="groupPicker"
          options={groupOptions}
          current={filter.group}
          onSelect={handleGroupSelect}
          disabled={!groupOptions.length}
          getSuggestionClasses={getGroupSuggestionClasses}
          selectText={t('SELECT_GROUP')}
          isSearchable
          inactive={!groupActive}
        />
        {zonePickerVisible && <OptionPicker<Zone>
          id="zonePicker"
          options={zoneOptions}
          current={filter.zone}
          onSelect={handleZoneSelect}
          disabled={!zoneOptions.length}
          selectText={t('SELECT_ZONE')}
          isSearchable
          inactive={!zoneActive}
        />}
      </ChildrenContainer>
      {
        props.additionalFilters && props.additionalFilters?.map(additionalFilter => (
          <ChildrenContainer key={additionalFilter.id}>
            {additionalFilter.title && <Title>{additionalFilter.title}</Title>}
            <OptionPicker<AdditionalFilterTypes>
              id={additionalFilter.id}
              options={additionalFilter.options}
              current={additionalFilter.current}
              onSelect={additionalFilter.handleFilterSelect}
              selectText={additionalFilter.selectText}
            />
          </ChildrenContainer>
        ))
      }
    </ParentContainer>
  );
}
